/**
 * Calculates optimal parameters based on workflow task count
 * Optimized for SFLAGA algorithm performance across different problem sizes
 * 
 * Scientific principles:
 * - Iterations scale with problem complexity: O(n log n) search space complexity
 * - Small problems converge faster: fewer iterations needed
 * - Large problems need more exploration: more iterations required
 * - Population scales with search space size: larger problems need more diversity
 * - Memeplex count scales with population: ~population/20 for optimal memeplex size
 * - Mutation rate decreases with problem size: maintain memeplex cohesion
 * 
 * Empirical findings:
 * - Very small problems (≤10): SFLAGA overhead may outweigh benefits
 * - Small problems (11-20): Reduced iterations sufficient for convergence
 * - Medium problems (21-50): Optimal range for SFLAGA
 * - Large problems (51-100): SFLAGA excels, needs more iterations
 * - Very large problems (101+): Maximum iterations for thorough search
 */
export function getOptimalParametersForTaskCount(taskCount: number) {
  if (taskCount <= 10) {
    // Very small problems: Minimal iterations, small population
    // Note: For very small problems, GA/Cultural may outperform SFLAGA
    // Reduced iterations to minimize overhead
    return {
      iterations: 80,          // Minimal: Small search space converges quickly
      initialPopulation: 120, // Small: Sufficient for small search space
      crossoverRate: 70,      // Lower: Less aggressive for small problems
      mutationRate: 12,       // Lower: Maintain stability
      memeplexCount: 6,       // Fewer memeplexes (~20 frogs each)
    };
  } else if (taskCount <= 20) {
    // Small problems: Reduced iterations, moderate population
    // SFLAGA can work but may not outperform simpler algorithms
    return {
      iterations: 120,        // Reduced: Small problems converge faster
      initialPopulation: 150, // Moderate: Balance between diversity and efficiency
      crossoverRate: 75,
      mutationRate: 15,       // Moderate: Some exploration needed
      memeplexCount: 8,       // Moderate memeplex count (~18-19 frogs each)
    };
  } else if (taskCount <= 50) {
    // Medium problems: Balanced defaults (optimal for SFLA-GA)
    // This is the sweet spot where SFLAGA performs best
    return {
      iterations: 200,        // Optimal: Sufficient for memeplex convergence
      initialPopulation: 250, // Optimal: Good diversity without overhead
      crossoverRate: 80,      // High: Leverage GA operators
      mutationRate: 15,       // Moderate: Balance exploration/exploitation
      memeplexCount: 12,      // Optimal: ~20-21 frogs per memeplex
    };
  } else if (taskCount <= 100) {
    // Large problems: Higher population, more iterations
    // SFLAGA excels here - needs more time for thorough search
    return {
      iterations: 300,        // Increased: Large search space needs more exploration
      initialPopulation: 350, // Larger: More diversity for complex search space
      crossoverRate: 85,      // Very high: Maximize GA exploitation
      mutationRate: 12,       // Lower: Maintain memeplex structure
      memeplexCount: 18,      // More memeplexes (~19-20 frogs each)
    };
  } else if (taskCount <= 500) {
    // Very large problems: Much higher population and iterations
    // Maximum exploration needed for complex search spaces
    return {
      iterations: 400,        // High: Complex search space requires thorough exploration
      initialPopulation: 500, // Large: Maximum diversity for large search space
      crossoverRate: 85,      // Very high: Leverage GA power
      mutationRate: 10,       // Lower: Stability over exploration
      memeplexCount: 25,      // Many memeplexes for parallel search (~20 frogs each)
    };
  } else {
    // Extremely large problems (500-1000): Maximum population and iterations
    // Full exploration required for very complex search spaces
    return {
      iterations: 500,        // Maximum: Very complex search space
      initialPopulation: 700, // Very large: Maximum diversity
      crossoverRate: 85,      // Very high: Maximum GA exploitation
      mutationRate: 10,       // Low: Maintain stability
      memeplexCount: 35,      // Maximum memeplexes for parallel exploration (~20 frogs each)
    };
  }
}

